import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/api";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const location = useLocation();
  const fromLocation = location.state?.from;

  const from = fromLocation
    ? `${fromLocation.pathname || ""}${fromLocation.search || ""}${fromLocation.hash || ""}`
    : "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  async function handlePasswordLogin(e) {
    e.preventDefault();
    setError("");

    try {
      const data = await api("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      // Store the JWT so api.js can send it
      localStorage.setItem("lumat_token", data.token);

      // Put the user record into context
      setUser(data.user);

      navigate(from, { replace: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Login failed");
    }
  }

  function handleMicrosoftLogin() {
    sessionStorage.setItem("post_login_redirect", from);

    const API_BASE =
      process.env.REACT_APP_API_URL || "/api";

    window.location.href = `${API_BASE}/auth/microsoft`;
  }

  return (
    <div
      className="
        flex items-center justify-center
        min-h-screen px-4 py-8
        bg-gray-100 dark:bg-slate-950
      "
    >
      <div
        className="
          w-full max-w-md
          bg-white dark:bg-slate-800
          border border-gray-200 dark:border-slate-700
          shadow-lg rounded-xl
          p-6 space-y-5
          text-gray-900 dark:text-gray-100
        "
      >
        {/* Heading */}
        <h1
          className="
            text-2xl font-bold text-center
            text-gray-900 dark:text-white
          "
        >
          Sign in to Nightjar
        </h1>

        {/* Microsoft SSO */}
        <button
          type="button"
          onClick={handleMicrosoftLogin}
          className="
            w-full
            bg-blue-600 hover:bg-blue-700
            text-white
            font-medium
            py-3 px-4
            rounded-lg
            transition-colors
          "
        >
          Sign in with Microsoft
        </button>

        {/* Separator */}
        <div className="flex items-center gap-3">
          <div className="flex-1 border-t border-gray-200 dark:border-slate-600" />

          <span className="text-sm text-gray-500 dark:text-gray-400">
            or
          </span>

          <div className="flex-1 border-t border-gray-200 dark:border-slate-600" />
        </div>

        {/* Username/password fallback */}
        <form onSubmit={handlePasswordLogin} className="space-y-4">

          {/* Email */}
          <div>
            <label
              htmlFor="login-email"
              className="
                block text-sm font-medium mb-1
                text-gray-700 dark:text-gray-200
              "
            >
              Email
            </label>

            <input
              id="login-email"
              type="email"
              placeholder="Enter your email"
              autoComplete="username"
              className="
                w-full
                border border-gray-300 dark:border-slate-600
                bg-white dark:bg-slate-700
                text-gray-900 dark:text-white
                placeholder-gray-400 dark:placeholder-gray-400
                px-3 py-2.5
                rounded-lg
                outline-none
                focus:ring-2 focus:ring-blue-500
                focus:border-blue-500
              "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="login-password"
              className="
                block text-sm font-medium mb-1
                text-gray-700 dark:text-gray-200
              "
            >
              Password
            </label>

            <input
              id="login-password"
              type="password"
              placeholder="Enter your password"
              autoComplete="current-password"
              className="
                w-full
                border border-gray-300 dark:border-slate-600
                bg-white dark:bg-slate-700
                text-gray-900 dark:text-white
                placeholder-gray-400 dark:placeholder-gray-400
                px-3 py-2.5
                rounded-lg
                outline-none
                focus:ring-2 focus:ring-blue-500
                focus:border-blue-500
              "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Error message */}
          {error && (
            <div
              role="alert"
              className="
                rounded-lg
                border border-red-200 dark:border-red-800
                bg-red-50 dark:bg-red-950
                text-red-700 dark:text-red-300
                px-3 py-2
                text-sm
              "
            >
              {error}
            </div>
          )}

          {/* Password login */}
          <button
            type="submit"
            className="
              w-full
              bg-gray-800 hover:bg-gray-900
              dark:bg-slate-600 dark:hover:bg-slate-500
              text-white
              font-medium
              py-3 px-4
              rounded-lg
              transition-colors
            "
          >
            Sign in with Username & Password
          </button>
        </form>
      </div>
    </div>
  );
}